// /src/utils/geo_location.js
// Smart Alert location utility.
//
// Priority:
// 1) Fresh browser/device geolocation (GPS / Wi-Fi / cellular as resolved by browser)
// 2) Previously trusted browser/device location from local cache
// 3) Coarse IP fallback from backend, only when no trusted device/browser fix exists
//
// Important:
// - IP must never beat or overwrite a trusted browser/device location.
// - The canonical frontend cache is "last_location" so location_manager.js and
//   this utility use the same stored fix.
// - Legacy "location_last_v2" is read only for one-time migration.
// - Cached location timestamps are preserved; reading/writing a cache must not
//   make an old location look new.
// - All public functions NEVER throw; they resolve to a normalized payload.
//
// Exports:
// - getBestLocation({ deadlineMs=2500, prefer="coarse" })
// - continueFineLocation({ timeoutMs=9000, signal })
// - warmUpLocation({ deadlineMs=1500 })
// - getHybridLocation()  // backward-compatible wrapper
// - getLastCachedLocation(maxAgeMs)

import axios from "axios";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL ||
  "https://chat-legal-683788781507.us-central1.run.app";

const CACHE_KEY = "last_location";
const LEGACY_CACHE_KEY = "location_last_v2";

const SOURCE_DEVICE = "device";
const SOURCE_WIFI = "wifi";
const SOURCE_BROWSER = "browser";
const SOURCE_IP = "ip";

// ---------- Public APIs ----------

/**
 * Return the best available location without allowing IP to outrank a trusted
 * browser/device fix.
 *
 * Device and IP requests may start together for latency, but selection is NOT
 * "first response wins":
 *
 *   device success -> use device
 *   device timeout/failure + trusted cache -> use trusted cache
 *   no trusted device/cache -> use IP as last resort
 *
 * A late device result may improve the trusted cache in the background.
 */
export async function getBestLocation({
  deadlineMs = 2500,
  prefer = "coarse",
} = {}) {
  try {
    const timeoutMs = Math.max(250, Number(deadlineMs) || 2500);

    // Read the canonical trusted last-known location first.
    const cached = getLastCachedLocation();

    // Start both for latency, but IP is never allowed to win merely because it
    // responds faster than navigator.geolocation.
    const deviceP = getDevicePosition({
      timeoutMs,
      highAccuracy: prefer !== "coarse",
    })
      .then(normalizeDevice)
      .catch(() => null);

    const ipP = getIPLocationFromBackend({
      timeoutMs: Math.min(1500, timeoutMs),
    })
      .then(normalizeIP)
      .catch(() => null);

    // Only device is allowed to win during the primary deadline window.
    const deviceWithinDeadline = await Promise.race([
      deviceP,
      createTimeout(timeoutMs).then(() => null),
    ]);

    if (isTrustedFix(deviceWithinDeadline)) {
      const deviceFix = stampAgeAndNormalize(deviceWithinDeadline);
      writeCache(deviceFix);
      return deviceFix;
    }

    // A real previously known browser/device location is more meaningful for
    // "Last Known Location" than a fresh coarse IP estimate.
    if (isTrustedFix(cached)) {
      // Let a late device result improve the cache, but never let IP replace it.
      improveTrustedCacheFromDevice(deviceP, cached).catch(() => {});
      return stampAgeAndNormalize(cached);
    }

    // No trusted browser/device history exists. IP is now allowed strictly as
    // the last-resort coarse fallback.
    const ipFix = await ipP;

    if (isUsableFix(ipFix)) {
      // Deliberately DO NOT write IP into last_location.
      improveTrustedCacheFromDevice(deviceP, null).catch(() => {});
      return stampAgeAndNormalize(ipFix);
    }

    // If IP also failed, give a late device result one final non-blocking chance
    // if it has already resolved.
    const lateDevice = await Promise.race([
      deviceP,
      Promise.resolve(null),
    ]);

    if (isTrustedFix(lateDevice)) {
      const deviceFix = stampAgeAndNormalize(lateDevice);
      writeCache(deviceFix);
      return deviceFix;
    }

    return stampAgeAndNormalize(makeUnknown("location_unavailable"));
  } catch {
    return stampAgeAndNormalize(makeUnknown("location_error"));
  }
}

/**
 * After an alert is created, try to obtain a finer browser/device fix.
 *
 * Only trusted browser/device fixes are allowed to update last_location.
 */
export async function continueFineLocation({
  timeoutMs = 9000,
  signal,
} = {}) {
  try {
    const pos = await getDevicePosition({
      timeoutMs,
      highAccuracy: true,
      signal,
    });

    const fine = stampAgeAndNormalize(
      normalizeDevice(pos)
    );

    const current = getLastCachedLocation();

    if (
      isTrustedFix(fine) &&
      (!current || isBetter(fine, current))
    ) {
      writeCache(fine);
      return fine;
    }

    return null;
  } catch {
    return null;
  }
}

/**
 * Used by UI controls to pre-warm browser geolocation and the trusted cache.
 */
export async function warmUpLocation({
  deadlineMs = 1500,
} = {}) {
  return getBestLocation({
    deadlineMs,
    prefer: "coarse",
  });
}

/**
 * Backward-compatible wrapper for existing callers.
 */
export async function getHybridLocation() {
  return getBestLocation({
    deadlineMs: 2500,
    prefer: "coarse",
  });
}

/**
 * Read the canonical trusted last-known browser/device location.
 *
 * Optionally enforce maxAgeMs. IP/unknown cache entries are intentionally
 * ignored and are never treated as trusted "Last Known Location".
 *
 * Also performs a one-time migration from legacy location_last_v2 when that
 * entry contains a trusted browser/device fix.
 */
export function getLastCachedLocation(maxAgeMs) {
  try {
    const primary = readCacheKey(CACHE_KEY);
    const legacy = readCacheKey(LEGACY_CACHE_KEY);

    const candidates = [
      primary,
      legacy,
    ].filter(isTrustedFix);

    if (!candidates.length) {
      return null;
    }

    let best = candidates[0];

    for (const candidate of candidates.slice(1)) {
      if (isBetter(candidate, best)) {
        best = candidate;
      }
    }

    const normalized = stampAgeAndNormalize(best);

    if (
      Number.isFinite(maxAgeMs) &&
      Number.isFinite(normalized.age_ms) &&
      normalized.age_ms > maxAgeMs
    ) {
      return null;
    }

    // Migrate trusted legacy data into the canonical key without changing the
    // original fix timestamp.
    if (!primary || isBetter(normalized, primary)) {
      writeCache(normalized);
    }

    try {
      localStorage.removeItem(LEGACY_CACHE_KEY);
    } catch {
      // ignore
    }

    return normalized;
  } catch {
    return null;
  }
}

// ---------- Internals ----------

function createTimeout(ms) {
  return new Promise((resolve) =>
    setTimeout(resolve, Math.max(0, ms))
  );
}

function getDevicePosition({
  timeoutMs = 3000,
  highAccuracy = false,
  signal,
} = {}) {
  if (
    typeof navigator === "undefined" ||
    !("geolocation" in navigator)
  ) {
    return Promise.reject(
      new Error("geolocation_unavailable")
    );
  }

  return new Promise((resolve, reject) => {
    let done = false;
    let abortHandler = null;

    const cleanup = () => {
      clearTimeout(timer);

      if (signal && abortHandler) {
        try {
          signal.removeEventListener(
            "abort",
            abortHandler
          );
        } catch {
          // ignore
        }
      }
    };

    const finishResolve = (value) => {
      if (done) return;
      done = true;
      cleanup();
      resolve(value);
    };

    const finishReject = (error) => {
      if (done) return;
      done = true;
      cleanup();
      reject(
        error || new Error("device_error")
      );
    };

    // Manual timeout guard. This fixes the prior bug where `done` was set to
    // true before onError(), causing the timeout path to return without reject.
    const timer = setTimeout(() => {
      finishReject(
        new Error("geolocation_timeout")
      );
    }, Math.max(250, timeoutMs));

    if (signal) {
      if (signal.aborted) {
        finishReject(
          new Error("aborted")
        );
        return;
      }

      abortHandler = () => {
        finishReject(
          new Error("aborted")
        );
      };

      signal.addEventListener(
        "abort",
        abortHandler,
        { once: true }
      );
    }

    try {
      navigator.geolocation.getCurrentPosition(
        finishResolve,
        finishReject,
        {
          enableHighAccuracy: !!highAccuracy,
          timeout: Math.max(250, timeoutMs),
          // Let the browser use a recent GPS/Wi-Fi/cellular cached fix quickly.
          maximumAge: 30000,
        }
      );
    } catch (error) {
      finishReject(error);
    }
  });
}

async function getIPLocationFromBackend({
  timeoutMs = 1500,
} = {}) {
  try {
    const res = await axios.get(
      `${BACKEND_URL}/api/location/ip`,
      {
        timeout: timeoutMs,
      }
    );

    const {
      latitude,
      longitude,
      city,
      region,
      country,
      accuracy,
      provider,
      precision,
      ip,
    } = res.data || {};

    return {
      lat: toNum(latitude),
      lon: toNum(longitude),
      city: city || null,
      region: region || null,
      country: country || null,
      accuracy: toNum(accuracy),
      source: SOURCE_IP,
      provider: provider || null,
      precision: precision || "coarse",
      ip: ip || null,
      timestamp: new Date().toISOString(),
    };
  } catch {
    return makeUnknown("ip_error");
  }
}

// ---------- Normalize helpers ----------

function normalizeDevice(pos) {
  if (!pos?.coords) {
    return makeUnknown("device_error");
  }

  const {
    latitude,
    longitude,
    accuracy,
  } = pos.coords || {};

  const positionTime =
    Number.isFinite(pos?.timestamp)
      ? new Date(pos.timestamp)
      : null;

  return {
    lat: toNum(latitude),
    lon: toNum(longitude),
    accuracy: toNum(accuracy),
    city: null,
    region: null,
    country: null,
    source: SOURCE_DEVICE,
    precision: "device",
    timestamp:
      positionTime && !Number.isNaN(positionTime.getTime())
        ? positionTime.toISOString()
        : new Date().toISOString(),
  };
}

function normalizeIP(ip) {
  if (
    !ip ||
    ip.source !== SOURCE_IP
  ) {
    return makeUnknown("ip_error");
  }

  return {
    lat: toNum(ip.lat),
    lon: toNum(ip.lon),
    accuracy: toNum(ip.accuracy),
    city: ip.city || null,
    region: ip.region || null,
    country: ip.country || null,
    source: SOURCE_IP,
    provider: ip.provider || null,
    precision: ip.precision || "coarse",
    ip: ip.ip || null,
    timestamp:
      ip.timestamp ||
      new Date().toISOString(),
  };
}

function stampAgeAndNormalize(loc) {
  const ts = toDate(
    loc?.timestamp
  );

  const ageMs = ts
    ? Date.now() - ts.getTime()
    : null;

  return {
    lat: toNum(loc?.lat),
    lon: toNum(loc?.lon),
    accuracy: toNum(loc?.accuracy),
    city: loc?.city ?? null,
    region: loc?.region ?? null,
    country: loc?.country ?? null,
    source: loc?.source || "unknown",
    provider: loc?.provider ?? null,
    precision: loc?.precision ?? null,
    ip: loc?.ip ?? null,
    age_ms:
      Number.isFinite(ageMs)
        ? Math.max(0, ageMs)
        : null,
    timestamp: ts
      ? ts.toISOString()
      : new Date().toISOString(),
  };
}

function isUsableFix(loc) {
  return (
    !!loc &&
    Number.isFinite(loc.lat) &&
    Number.isFinite(loc.lon)
  );
}

function isTrustedFix(loc) {
  if (!isUsableFix(loc)) {
    return false;
  }

  return [
    SOURCE_DEVICE,
    SOURCE_WIFI,
    SOURCE_BROWSER,
  ].includes(
    String(loc.source || "").toLowerCase()
  );
}

function sourceRank(source) {
  const value = String(
    source || ""
  ).toLowerCase();

  if (value === SOURCE_DEVICE) return 4;
  if (value === SOURCE_WIFI) return 3;
  if (value === SOURCE_BROWSER) return 3;
  if (value === SOURCE_IP) return 1;

  return 0;
}

function isBetter(a, b) {
  const left = stampAgeAndNormalize(a);
  const right = stampAgeAndNormalize(b);

  if (!isUsableFix(left)) return false;
  if (!isUsableFix(right)) return true;

  const leftRank = sourceRank(
    left.source
  );
  const rightRank = sourceRank(
    right.source
  );

  if (leftRank !== rightRank) {
    return leftRank > rightRank;
  }

  const leftAccuracy =
    Number.isFinite(left.accuracy)
      ? left.accuracy
      : Infinity;

  const rightAccuracy =
    Number.isFinite(right.accuracy)
      ? right.accuracy
      : Infinity;

  if (leftAccuracy !== rightAccuracy) {
    return leftAccuracy < rightAccuracy;
  }

  const leftTime =
    toDate(left.timestamp)?.getTime() || 0;

  const rightTime =
    toDate(right.timestamp)?.getTime() || 0;

  return leftTime > rightTime;
}

function makeUnknown(reason) {
  return {
    lat: null,
    lon: null,
    accuracy: null,
    city: null,
    region: null,
    country: null,
    source: reason
      ? `unknown:${reason}`
      : "unknown",
    provider: null,
    precision: null,
    ip: null,
    timestamp: new Date().toISOString(),
  };
}

function readCacheKey(key) {
  try {
    const raw = localStorage.getItem(
      key
    );

    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);
    const normalized = stampAgeAndNormalize(
      parsed
    );

    return isUsableFix(normalized)
      ? normalized
      : null;
  } catch {
    return null;
  }
}

function writeCache(loc) {
  try {
    const normalized = stampAgeAndNormalize(
      loc
    );

    // Canonical last_location is reserved for trusted browser/device fixes.
    // Never let IP become the user's stored "Last Known Location".
    if (!isTrustedFix(normalized)) {
      return false;
    }

    const payload = {
      lat: normalized.lat,
      lon: normalized.lon,
      accuracy: normalized.accuracy,
      city: normalized.city,
      region: normalized.region,
      country: normalized.country,
      source: normalized.source,
      provider: normalized.provider,
      precision: normalized.precision,
      // Preserve the actual fix time. Do not refresh the timestamp merely
      // because the cache entry was read or rewritten.
      timestamp: normalized.timestamp,
    };

    localStorage.setItem(
      CACHE_KEY,
      JSON.stringify(payload)
    );

    return true;
  } catch {
    return false;
  }
}

async function improveTrustedCacheFromDevice(
  deviceP,
  current
) {
  try {
    const candidate = await deviceP;

    if (
      isTrustedFix(candidate) &&
      (!current || isBetter(candidate, current))
    ) {
      writeCache(candidate);
    }
  } catch {
    // ignore
  }
}

// ---------- Utils ----------

function toNum(v) {
  if (
    v === null ||
    v === undefined ||
    v === ""
  ) {
    return null;
  }

  const n = Number(v);
  return Number.isFinite(n)
    ? n
    : null;
}

function toDate(v) {
  try {
    const d = new Date(v);

    return Number.isNaN(
      d.getTime()
    )
      ? null
      : d;
  } catch {
    return null;
  }
}
